import type { SensorStatus } from "@sitescene/types";

import { toTitleCase } from "../../utils/shared/strings";

export type CreatePointMarkerFeatureParams = {
	coordinates: [number, number];
	id: string;
	properties?: GeoJSON.GeoJsonProperties;
};

export type MarkerFeatureKind =
	| "location"
	| "locationCluster"
	| "sensor"
	| "zoneCluster";

/** Builds a GeoJSON Point feature for Map markers. */
export const createPointMarkerFeature = ({
	coordinates,
	id,
	properties = null,
}: CreatePointMarkerFeatureParams): GeoJSON.Feature<GeoJSON.Point> => ({
	geometry: {
		coordinates,
		type: "Point",
	},
	id,
	properties,
	type: "Feature",
});

export type CreateSensorMarkerFeatureParams = {
	coordinates: [number, number];
	dimmed?: boolean;
	id: string;
	lastInputReceived?: Date | null | string;
	locationId?: string;
	name: string;
	status: SensorStatus;
	/** Original geocode before spiderfy offset (lng, lat). */
	trueCoordinates?: [number, number];
	type: string;
	zoneId?: string;
};

/** Builds a Point feature matching the sensor MapMarker / MapMarkerPopup contract. */
export const createSensorMarkerFeature = ({
	coordinates,
	dimmed,
	id,
	lastInputReceived,
	locationId,
	name,
	status,
	trueCoordinates,
	type,
	zoneId,
}: CreateSensorMarkerFeatureParams): GeoJSON.Feature<GeoJSON.Point> =>
	createPointMarkerFeature({
		coordinates,
		id,
		properties: {
			...(dimmed ? { dimmed: true } : {}),
			id,
			kind: "sensor" satisfies MarkerFeatureKind,
			lastInputReceived: lastInputReceived ?? null,
			locationId,
			name: toTitleCase(name),
			status,
			trueCoordinates,
			type,
			zoneId,
		},
	});

export type CreateLocationMarkerFeatureParams = {
	coordinates: [number, number];
	id: string;
	name: string;
};

/** Builds a Point feature for a location pin (default marker style). */
export const createLocationMarkerFeature = ({
	coordinates,
	id,
	name,
}: CreateLocationMarkerFeatureParams): GeoJSON.Feature<GeoJSON.Point> =>
	createPointMarkerFeature({
		coordinates,
		id,
		properties: {
			id,
			kind: "location" satisfies MarkerFeatureKind,
			name,
		},
	});

export type CreateClusterMarkerFeatureParams = {
	coordinates: [number, number];
	count: number;
	id: string;
	kind: "locationCluster" | "zoneCluster";
	locationId?: string;
	/** Setup gaps for location clusters (installation / zone / sensors). */
	missingSetup?: string[];
	name: string;
	sensorOffline: number;
	sensorOnline: number;
	sensorWarning: number;
	/**
	 * Original geocode before spiderfy offset (lng, lat).
	 * Zone cards that share one real location keep this so camera fit can
	 * detect stacked zones even after display coords are spread in a ring.
	 */
	trueCoordinates?: [number, number];
	zoneId?: string;
};

/** Builds a Point feature for a location or zone cluster card on the map. */
export const createClusterMarkerFeature = ({
	coordinates,
	count,
	id,
	kind,
	locationId,
	missingSetup,
	name,
	sensorOffline,
	sensorOnline,
	sensorWarning,
	trueCoordinates,
	zoneId,
}: CreateClusterMarkerFeatureParams): GeoJSON.Feature =>
	createPointMarkerFeature({
		coordinates,
		id,
		properties: {
			count,
			id,
			kind,
			locationId,
			missingSetup,
			name: toTitleCase(name),
			sensorOffline,
			sensorOnline,
			sensorWarning,
			trueCoordinates,
			zoneId,
		},
	});

export const isSensorMarkerFeature = (feature: GeoJSON.Feature): boolean =>
	feature.properties?.kind === "sensor";

export const isClusterMarkerFeature = (feature: GeoJSON.Feature): boolean => {
	const kind = feature.properties?.kind;
	return kind === "locationCluster" || kind === "zoneCluster";
};
